// SGNL-209 — Focus List configuration.
//
// Replaces the hardcoded `probability_min: 50` from SGNL-193 with a
// deploy-tunable {min, max} band so operators can tighten the preset
// without a code change. Operator feedback:
//   - probability >= 99 → already signed (no focus needed)
//   - probability 95   → signature pending
//   - probability 80-90 → the actual "needs follow-up" band
// Default {min: 50, max: 90} covers the broader "deals worth attention"
// band; tighter cohorts override per-deploy.
//
// Config sources (in priority order):
//   1. `import.meta.env.VITE_FOCUS_LIST_PROBABILITY_MIN` (build-time)
//   2. `import.meta.env.VITE_FOCUS_LIST_PROBABILITY_MAX` (build-time)
//   3. Module-level defaults (50, 90).
// Vite inlines the values at build time, so a deploy-time change is
// `VITE_FOCUS_LIST_PROBABILITY_MIN=80 VITE_FOCUS_LIST_PROBABILITY_MAX=95 npm run build`.

interface FocusListBand {
  min: number;
  max: number;
}

const DEFAULTS: FocusListBand = { min: 50, max: 90 };

function parseProbability(raw: unknown, fallback: number): number {
  if (typeof raw !== "string" || raw === "") return fallback;
  const n = Number(raw);
  // Reject NaN / Infinity / out-of-range so a malformed env var falls
  // back to the default instead of silently breaking the filter.
  if (!Number.isFinite(n) || n < 0 || n > 100) return fallback;
  return n;
}

let _cached: FocusListBand | null = null;

/** Read the Focus List band — cached on first call so the same env
 *  resolution doesn't run on every click. Exposed for tests via the
 *  `_resetForTests` seam below. */
export function getFocusListBand(): FocusListBand {
  if (_cached) return _cached;
  const env = import.meta.env;
  const min = parseProbability(env.VITE_FOCUS_LIST_PROBABILITY_MIN, DEFAULTS.min);
  const max = parseProbability(env.VITE_FOCUS_LIST_PROBABILITY_MAX, DEFAULTS.max);
  // Guard: a configuration where min > max would always-empty the
  // Focus list — surface the bad config by falling back to defaults.
  if (min > max) {
    // Surfacing intentionally minimal — operators see the unexpected
    // band; a console warning helps the deploy debugger.
    if (typeof console !== "undefined") {
      console.warn(
        `[SGNL-209] FOCUS_LIST band misconfigured (min=${min} > max=${max}) — falling back to defaults`,
      );
    }
    _cached = { ...DEFAULTS };
  } else {
    _cached = { min, max };
  }
  return _cached;
}

/** Test-only seam — reset the cached band so a test can re-stub
 *  `import.meta.env` and re-read. Not used in prod. */
export function _resetFocusListConfigForTests(): void {
  _cached = null;
}
